import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { GovernanceFlow } from "@/components/site/GovernanceFlow";
import { CAPABILITIES } from "@/data/capabilities";
import { AIRO } from "@/constants/testIds";

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
};

const Section = ({ children, className = "" }) => (
  <section className={`py-20 md:py-32 border-t border-[#141414] ${className}`}>
    <div className="max-w-5xl mx-auto px-6 md:px-12">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <motion.div {...reveal} className="mono-label text-cyan-400 mb-8">{children}</motion.div>
);

const Statement = ({ children, className = "" }) => (
  <motion.h2 {...reveal} className={`font-heading font-extrabold tracking-tighter leading-[1] text-3xl md:text-5xl lg:text-6xl ${className}`}>
    {children}
  </motion.h2>
);

const Prose = ({ children }) => (
  <motion.div {...reveal} className="max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed space-y-6">
    {children}
  </motion.div>
);

const Ticks = ({ items, accent = false }) => (
  <div className="flex flex-wrap gap-x-8 gap-y-3">
    {items.map((t) => (
      <span key={t} className={`font-heading text-xl md:text-2xl ${accent ? "text-white" : "text-zinc-600"}`}>{t}</span>
    ))}
  </div>
);

export default function Story() {
  return (
    <div>
      {/* HERO */}
      <section className="pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_0.85fr] gap-14 lg:gap-16 items-center">
          <div>
            <motion.div {...reveal} className="mono-label text-cyan-400 mb-6">AIROTECH — OUR STORY</motion.div>
            <motion.div {...reveal} className="mono-label text-zinc-500 mb-8">THE COMPANY BEHIND THE BUILD</motion.div>
            <motion.h1 {...reveal} className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl md:text-7xl">
              We turn ideas<br />into products.
            </motion.h1>
            <motion.p {...reveal} className="mt-6 mono-label text-zinc-400 leading-relaxed">
              AIROTECH INC. · A DIGITAL PRODUCT STUDIO · BUSINESS · TECHNOLOGY · CREATIVITY · AI
            </motion.p>
          </div>
          <motion.div {...reveal} className="relative">
            <div className="absolute -inset-6 rounded-full bg-cyan-500/[0.06] blur-3xl" />
            <div className="relative w-full max-w-md mx-auto aspect-square rounded-full border border-[#1c1c1c] bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
              <img
                src="/airotech-mark.png"
                alt="AIROTECH"
                className="w-1/2 h-1/2 object-contain opacity-90"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* BIG STATEMENT */}
      <Section>
        <Statement>
          AIROTECH wasn't built<br />
          <span className="text-zinc-600">to become another software company.</span><br />
          It was built to make <span className="text-cyan-400">building possible.</span>
        </Statement>
      </Section>

      {/* SHORT STORY */}
      <Section>
        <Prose>
          <p>
            AIROTECH was founded at the intersection of business, technology, sales, communication, creativity,
            entrepreneurship and product development. Across those disciplines, one truth became
            impossible to ignore: there are people everywhere with ideas that could become something extraordinary.
          </p>
          <p>A business. A platform. An application. A new service. A creative product. A better way to solve a problem.</p>
          <p>
            But having the idea is only the beginning. For a long time, the distance between
            <span className="text-white"> "I have an idea"</span> and
            <span className="text-white"> "people can actually use it"</span> was enormous. It required technical people,
            designers, product people, infrastructure, money and time.
          </p>
          <p className="text-white">Then artificial intelligence changed the equation.</p>
        </Prose>
      </Section>

      {/* EVERYONE CAN PROMPT */}
      <Section className="bg-[#070707]">
        <Statement className="mb-12">
          Everyone can prompt.<br /><span className="text-zinc-600">Not everyone can build.</span>
        </Statement>
        <div className="grid md:grid-cols-2 gap-12">
          <motion.div {...reveal}>
            <div className="mono-label text-zinc-500 mb-6">AI CAN GENERATE</div>
            <Ticks items={["JavaScript", "Python", "React", "APIs", "Database schemas", "Interfaces", "Entire applications"]} />
            <p className="mt-8 text-zinc-500">And that's incredible. But generating software isn't the same thing as engineering a product.</p>
          </motion.div>
          <motion.div {...reveal}>
            <div className="mono-label text-cyan-400 mb-6">A PRODUCT HAS TO</div>
            <ul className="space-y-2.5 text-base md:text-lg text-zinc-300">
              {["Work", "Solve the right problem", "Make business sense", "Make sense to the people using it",
                "Handle real data", "Connect to other systems", "Be secure", "Survive errors",
                "Be deployable", "Be maintainable", "Be measurable"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" /> {t}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
        <motion.p {...reveal} className="mt-14 font-heading font-extrabold tracking-tighter text-3xl md:text-5xl">
          And when people actually start using it: <span className="text-cyan-400">it has to be able to grow.</span>
        </motion.p>
        <motion.p {...reveal} className="mt-6 text-lg text-zinc-400">That's where experience matters. That's where AIROTECH comes in.</motion.p>
      </Section>

      {/* WHY AIROTECH EXISTS */}
      <Section>
        <Eyebrow>WHY AIROTECH EXISTS</Eyebrow>
        <Statement className="mb-10">
          What happens when modern AI is combined with real business experience, technology, creativity and execution?
        </Statement>
        <Prose>
          <p>The answer is a new kind of development environment. A place where someone doesn't need to arrive with a technical specification, a CTO, a development team, a product manager or a complete architecture.</p>
        </Prose>
        <motion.p {...reveal} className="mt-10 font-heading text-4xl md:text-6xl font-extrabold tracking-tighter">
          They can arrive with <span className="text-cyan-400">an idea.</span>
        </motion.p>
        <motion.p {...reveal} className="mt-6 text-lg text-zinc-400">And we can help turn that idea into something real.</motion.p>
      </Section>

      {/* DISCIPLINES */}
      <Section className="bg-[#070707]">
        <Eyebrow>BUSINESS + TECHNOLOGY + CREATIVITY + AI</Eyebrow>
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-8">
          {[
            ["Business", "asks what should exist and why."],
            ["Product", "determines what needs to be built."],
            ["Design", "determines how people experience it."],
            ["Engineering", "determines how it works."],
            ["Architecture", "determines how everything fits together."],
            ["AI", "expands what can be built and automated."],
            ["Operations", "makes it work in the real world."],
            ["Go to market", "gives the product a path to the people it's built for."],
          ].map(([k, v], i) => (
            <motion.div key={k} {...reveal} transition={{ ...reveal.transition, delay: (i % 2) * 0.05 }} className="border-b border-[#161616] pb-6">
              <span className="font-heading text-xl md:text-2xl text-white">{k}</span>
              <span className="text-zinc-500 text-base md:text-lg"> {v}</span>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* HUMAN IN THE LOOP */}
      <Section>
        <Eyebrow>THE HUMAN STAYS IN THE LOOP</Eyebrow>
        <Statement className="mb-10">
          We build with AI.<br /><span className="text-zinc-600">We don't hand the product over to AI.</span>
        </Statement>
        <Prose>
          <p>AI can accelerate development. It can analyze, generate, recommend and automate. But there are decisions that should remain accountable to people. AIROTECH believes the strongest systems combine machine capability with human judgment — so we design for human-in-the-loop architecture where it matters.</p>
        </Prose>
        <div className="mt-12 grid md:grid-cols-2 gap-12">
          <motion.div {...reveal}>
            <div className="mono-label text-zinc-500 mb-6">AI CAN</div>
            <Ticks items={["Analyze", "Classify", "Recommend", "Automate", "Detect patterns", "Surface possibilities"]} />
          </motion.div>
          <motion.div {...reveal}>
            <div className="mono-label text-cyan-400 mb-6">PEOPLE CAN</div>
            <Ticks items={["Review", "Question", "Approve", "Reject", "Override", "Escalate", "Take responsibility"]} accent />
          </motion.div>
        </div>
        <motion.p {...reveal} className="mt-14 font-heading font-extrabold tracking-tighter text-3xl md:text-5xl">
          The objective isn't to remove people from the system. <span className="text-cyan-400">It's to make people more capable.</span>
        </motion.p>
      </Section>

      {/* GOVERNANCE */}
      <Section className="bg-[#070707]">
        <Eyebrow>GOVERNANCE IS PART OF THE PRODUCT</Eyebrow>
        <Statement className="mb-10">
          AI shouldn't just be powerful.<br /><span className="text-cyan-400">It should be governable.</span>
        </Statement>
        <Prose>
          <p>As AI becomes capable of influencing decisions, routing work, communicating with customers and operating business processes, one question becomes unavoidable: who is governing what the AI is allowed to do?</p>
          <p>AI governance is one of AIROTECH's areas of focus and thought leadership. We believe intelligent systems should be designed with policy, boundaries, permissions and human oversight from the start — not simply given access to a system and told "go."</p>
        </Prose>
        <motion.div {...reveal} className="mt-10">
          <div className="mono-label text-zinc-500 mb-5">DESIGNED WITH</div>
          <Ticks items={["Policy", "Boundaries", "Permissions", "Auditability", "Observability", "Human oversight", "Reversibility", "Controlled promotion", "Accountability", "Clear decision paths"]} />
        </motion.div>
        <motion.div {...reveal} className="mt-14">
          <div className="mono-label text-zinc-500 mb-6">THE ARCHITECTURE SHOULD DETERMINE</div>
          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
            {["What can it see?", "What can it do?", "What decisions can it make?", "What requires human approval?",
              "What happens when it gets something wrong?", "Can the decision be explained?", "Can the action be reversed?",
              "Can the system be observed?", "Can governance evolve as the technology evolves?"].map((q, i) => (
              <motion.p key={q} {...reveal} transition={{ ...reveal.transition, delay: (i % 2) * 0.04 }}
                className="text-base md:text-lg text-zinc-300 border-b border-[#161616] pb-4">{q}</motion.p>
            ))}
          </div>
        </motion.div>
      </Section>

      {/* AI ASSISTANCE -> GOVERNANCE */}
      <Section>
        <Statement className="mb-10">
          From AI assistance<br /><span className="text-cyan-400">to AI governance.</span>
        </Statement>
        <Prose>
          <p>We don't treat AI governance as paperwork added after the product is built. Governance belongs in the architecture — systems where intelligence, policy and human authority work together. That philosophy influences how we approach intelligent products, automation and enterprise systems.</p>
        </Prose>
        <motion.div {...reveal} className="mt-12">
          <GovernanceFlow />
        </motion.div>
      </Section>

      {/* PHILOSOPHY */}
      <Section className="bg-[#070707]">
        <Eyebrow>OUR PHILOSOPHY</Eyebrow>
        <div className="space-y-5">
          {["AI should amplify human capability.",
            "Humans should retain meaningful authority.",
            "Governance should exist before enforcement.",
            "Systems should be observable.",
            "Important decisions should be reversible when possible.",
            "Technology should serve people, not the other way around."].map((t, i) => (
            <motion.p key={t} {...reveal} transition={{ ...reveal.transition, delay: i * 0.04 }}
              className="font-heading text-2xl md:text-4xl text-zinc-500 hover:text-white transition-colors duration-300">{t}</motion.p>
          ))}
        </div>
        <motion.p {...reveal} className="mt-12 text-lg text-zinc-400 max-w-2xl">
          That's the philosophy we're bringing into the next generation of products.
        </motion.p>
      </Section>

      {/* CAPABILITY MATRIX */}
      <Section>
        <Eyebrow>WHAT WE SPECIALIZE IN</Eyebrow>
        <Statement className="mb-6">Taking complex ideas and turning them into structured, functional, scalable products.</Statement>
        <Prose><p>Our capabilities sit at the intersection of business, product, technology, creativity, artificial intelligence and go to market execution.</p></Prose>

        <div className="mt-16 border-t border-[#141414]">
          {CAPABILITIES.map((group, i) => (
            <motion.div key={group.title} {...reveal} transition={{ ...reveal.transition, delay: (i % 3) * 0.04 }}
              className="grid md:grid-cols-[240px_1fr] gap-4 md:gap-10 py-8 md:py-9 border-b border-[#141414]">
              <div>
                <div className="mono-label text-zinc-600 mb-2">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-heading text-lg md:text-xl text-white leading-snug">{group.title}</h3>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 content-start">
                {group.items.map((it) => (
                  <span key={it} className="text-sm text-zinc-500 hover:text-cyan-300 transition-colors duration-200">{it}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SIGNATURE STATEMENT */}
      <section className="py-28 md:py-44 border-t border-[#141414] bg-[#070707]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.h2 {...reveal} className="font-heading font-extrabold tracking-tighter leading-[0.98] text-4xl md:text-6xl lg:text-7xl">
            Specialization isn't about<br />knowing one technology.<br />
            <span className="text-cyan-400">It's about knowing how the<br />pieces work together.</span>
          </motion.h2>
        </div>
      </section>

      {/* ONE SYSTEM */}
      <Section>
        <Prose>
          <p>A programming language is a tool. A framework is a tool. An API is a tool. A database is a tool. An AI model is a tool.</p>
          <p>The real capability is understanding which tools belong together, why they belong together, how they should interact, and what the finished system needs to accomplish.</p>
        </Prose>
        <motion.div {...reveal} className="mt-10">
          <div className="mono-label text-zinc-500 mb-5">A SUCCESSFUL PRODUCT IS</div>
          <Ticks items={["the business model", "the user experience", "the architecture", "the data", "the integrations", "the infrastructure", "the security", "the automation", "the economics", "the path to market"]} />
          <p className="mt-8 font-heading text-2xl md:text-4xl font-extrabold tracking-tighter text-white">
            working together as one system.
          </p>
          <p className="mt-4 mono-label text-cyan-400">THAT'S WHERE AIROTECH SPECIALIZES.</p>
        </motion.div>
      </Section>

      {/* FROM IDEA TO EXECUTION */}
      <Section className="bg-[#070707]">
        <Eyebrow>FROM IDEA TO EXECUTION</Eyebrow>
        <Statement className="mb-12">We don't just ask "can we build this?"</Statement>
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-5">
          {["What problem are we solving?", "Who is it for?", "What should the first version actually contain?",
            "What shouldn't be built yet?", "How should the system work?", "What technology should support it?",
            "What happens when something fails?", "How does it make money?", "How does it reach the market?",
            "What happens when it grows?"].map((q, i) => (
            <motion.div key={q} {...reveal} transition={{ ...reveal.transition, delay: (i % 2) * 0.04 }}
              className="text-lg md:text-xl text-zinc-300 border-b border-[#161616] pb-4">{q}</motion.div>
          ))}
        </div>
        <motion.p {...reveal} className="mt-12 text-lg text-zinc-400 max-w-2xl">
          That is the difference between building software and building a product.
        </motion.p>
      </Section>

      {/* THE WORK SPEAKS */}
      <Section>
        <Statement className="mb-8">The work speaks for itself.</Statement>
        <Prose>
          <p>We could turn this page into a résumé and list every platform, application and system we've built. We'd rather let you explore them yourself.</p>
        </Prose>
        <motion.div {...reveal} className="mt-12 rounded-2xl border border-[#161616] bg-[#0a0a0a] p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="font-heading text-2xl md:text-3xl text-white">See what AIROTECH has built.</div>
            <p className="mt-3 text-zinc-500 max-w-lg">Explore our flagship products, platforms and technology across the AIROTECH ecosystem.</p>
          </div>
          <a href="https://airotech-inc.com" target="_blank" rel="noopener noreferrer" data-testid={AIRO.exploreInc}
            className="group inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-300 shrink-0">
            Explore AIROTECH Inc.
            <ArrowUpRight className="h-4 w-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
          </a>
        </motion.div>
      </Section>

      {/* BIGGER THAN ONE PERSON */}
      <Section className="bg-[#070707]">
        <Statement className="mb-10">AIROTECH is bigger than any one person.</Statement>
        <Prose>
          <p>The vision began with a single idea — but it was never meant to depend on a single person. AIROTECH is built as a company: a discipline-driven practice where business, product, engineering, design, AI and go-to-market work toward the same mission.</p>
        </Prose>
        <motion.div {...reveal} className="mt-10">
          <Ticks items={["Technology", "Product", "Engineering", "Design", "Business", "AI", "Creative", "Operations", "Strategy", "Go to market"]} />
        </motion.div>
        <motion.p {...reveal} className="mt-14 font-heading font-extrabold tracking-tighter text-4xl md:text-6xl text-cyan-400">
          Operators on one accord.
        </motion.p>
      </Section>

      {/* WHY THAT MATTERS */}
      <Section>
        <Eyebrow>WHY THAT MATTERS</Eyebrow>
        <Statement className="mb-10">The best products aren't built from one skill. They're built when different skills understand one another.</Statement>
        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
          {["The business strategist has to understand the technology.",
            "The technologist has to understand the business.",
            "The designer has to understand the user.",
            "The engineer has to understand the system.",
            "The strategist has to understand the market.",
            "The builder has to understand what happens after launch.",
            "And everyone has to understand the product."].map((t, i) => (
            <motion.p key={t} {...reveal} transition={{ ...reveal.transition, delay: (i % 2) * 0.04 }}
              className="text-base md:text-lg text-zinc-400 border-b border-[#161616] pb-4">{t}</motion.p>
          ))}
        </div>
      </Section>

      {/* OPPORTUNITY */}
      <Section className="bg-[#070707]">
        <Eyebrow>THE OPPORTUNITY AI CREATED</Eyebrow>
        <Prose>
          <p>There has never been a better time to have an idea. AI is lowering the barrier to experimentation, increasing development speed, changing the economics of software and putting capabilities that once belonged only to large technical organizations into the hands of ordinary people.</p>
          <p>But accessibility creates another challenge.</p>
        </Prose>
        <motion.p {...reveal} className="mt-10 font-heading font-extrabold tracking-tighter text-4xl md:text-6xl">
          Everyone can build something.
        </motion.p>
        <motion.p {...reveal} className="mt-8 max-w-2xl text-lg text-zinc-400">
          The world still needs people who understand what should be built, how it should be built, how all the pieces fit together, and how to make it work in the real world. That's the opportunity we see.
        </motion.p>
      </Section>

      {/* WHY MVP EXISTS */}
      <Section>
        <Eyebrow>THIS IS WHY AIROTECH MVP EXISTS</Eyebrow>
        <div className="space-y-4">
          {["For the founder with an idea.",
            "For the business owner who sees a better way.",
            "For the creator with a product that doesn't exist yet.",
            "For the organization that needs software built around the way it actually works.",
            "For the person who knows what they want to accomplish but doesn't know how to turn it into technology."].map((t, i) => (
            <motion.p key={t} {...reveal} transition={{ ...reveal.transition, delay: i * 0.04 }}
              className="font-heading text-xl md:text-3xl text-zinc-500 hover:text-white transition-colors duration-300">{t}</motion.p>
          ))}
        </div>
        <motion.p {...reveal} className="mt-12 max-w-2xl text-lg text-zinc-400">
          You shouldn't have to become a software engineer before you're allowed to build your idea.
        </motion.p>
        <motion.p {...reveal} className="mt-8 font-heading font-extrabold tracking-tighter text-4xl md:text-6xl">
          Bring us the idea.<br /><span className="text-cyan-400">We'll help build the product.</span>
        </motion.p>
      </Section>

      {/* MISSION */}
      <section className="py-28 md:py-44 border-t border-[#141414] bg-[#070707]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div {...reveal} className="mono-label text-zinc-500 mb-10">ONE PROJECT AT A TIME</motion.div>
          <Prose>
            <p>We believe some of the world's best ideas are sitting inside people's heads right now. Some will never get built — not because the ideas aren't valuable, but because the distance between vision and execution has historically been too great.</p>
            <p>AI is changing that. AIROTECH exists to help close that distance.</p>
          </Prose>
          <motion.div {...reveal} className="mt-10">
            <Ticks items={["Business", "Technology", "Creativity", "Artificial intelligence", "Engineering", "Strategy", "People", "Execution"]} />
          </motion.div>
          <motion.h2 {...reveal} className="mt-14 font-heading font-extrabold tracking-tighter leading-[0.98] text-4xl md:text-6xl lg:text-7xl">
            Improving the world<br /><span className="text-cyan-400">one project at a time.</span>
          </motion.h2>
        </div>
      </section>

      {/* CONNECT / DUAL CTA */}
      <section className="py-28 md:py-40 border-t border-[#141414]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div {...reveal} className="mono-label text-cyan-400 mb-8">LET'S TALK ABOUT YOUR IDEA</motion.div>
          <motion.p {...reveal} className="max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed">
            Sometimes the best way to understand what you're trying to build is simply to have a conversation.
            We're always interested in meeting people who are thinking differently, solving difficult problems or
            trying to turn an idea into something real.
          </motion.p>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <motion.div {...reveal}>
              <Link
                to="/why"
                data-testid={AIRO.storyEcosystem}
                className="group block h-full rounded-2xl border border-[#161616] bg-[#0a0a0a] p-8 md:p-10 hover:border-cyan-400/30 transition-colors duration-300"
              >
                <div className="mono-label text-zinc-500 mb-6">WHY AIROTECH</div>
                <div className="font-heading text-2xl md:text-3xl text-white">See why teams build with us.</div>
                <div className="text-zinc-500 mt-1">Modern AI, real product experience, one accountable partner.</div>
                <div className="mt-8 mono-label text-cyan-400 inline-flex items-center gap-2">
                  Why AIROTECH
                  <ArrowUpRight className="h-4 w-4 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                </div>
              </Link>
            </motion.div>

            <motion.div {...reveal} className="rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.04] p-8 md:p-10 flex flex-col">
              <div className="mono-label text-cyan-300 mb-6">BUILD WITH AIROTECH</div>
              <div className="font-heading text-2xl md:text-3xl text-white">Have a product idea?</div>
              <div className="text-zinc-400 mt-1">You have the idea. We will build it.</div>
              <Link
                to="/start"
                data-testid={AIRO.storyStart}
                className="group mt-8 self-start inline-flex items-center gap-2 bg-white text-black font-medium px-7 py-4 rounded-full mono-label hover:bg-cyan-400 transition-colors duration-300"
              >
                Start Your Product Journey
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
